import { create } from "zustand";
import { persist } from "zustand/middleware";
import { api } from "../lib/api";
import type { CitationStyle } from "../lib/citationFormat";

export interface SearchResult {
  title: string;
  authors: string[];
  year: number | null;
  abstract: string | null;
  url: string | null;
  doi: string | null;
  journal: string | null;
}

export interface BibEntry {
  type: string;
  key: string;
  title: string;
  authors: string[];
  year: string | null;
  journal: string | null;
  doi: string | null;
  url: string | null;
  abstract: string | null;
}

interface CitationState {
  bibEntries: BibEntry[];
  searchQuery: string;
  searchResults: SearchResult[];
  searching: boolean;
  searchError: string | null;
  addError: string | null;
  documentCiteKeys: string[];
  citationStyle: CitationStyle;
  semanticScholarApiKey: string;

  setSearchQuery: (query: string) => void;
  search: (query: string) => Promise<void>;
  addCitation: (paper: SearchResult) => Promise<string | null>;
  removeCitation: (key: string) => void;
  clearSearch: () => void;
  clearAddError: () => void;
  parseDocumentCitations: (content: string) => void;
  setCitationStyle: (style: CitationStyle) => void;
  setSemanticScholarApiKey: (key: string) => void;
}

function extractCiteKeys(content: string): string[] {
  const re = /\\cite\{([^}]+)\}/g;
  const keys = new Set<string>();
  let match: RegExpExecArray | null;
  while ((match = re.exec(content)) !== null) {
    for (const k of match[1].split(",")) {
      const trimmed = k.trim();
      if (trimmed) keys.add(trimmed);
    }
  }
  return Array.from(keys);
}

function parseBibtexKey(bibtex: string): string | null {
  const match = bibtex.match(/@\w+\{([^,]+)/);
  return match ? match[1].trim() : null;
}

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1500;

function isRateLimitError(error: string): boolean {
  return /rate.?limit/i.test(error) || /429/i.test(error) || /wait/i.test(error);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const useCitationStore = create<CitationState>()(
  persist(
    (set, get) => ({
      bibEntries: [],
      searchQuery: "",
      searchResults: [],
      searching: false,
      searchError: null,
      addError: null,
      documentCiteKeys: [],
      citationStyle: "apa" as CitationStyle,
      semanticScholarApiKey: "",

      setSearchQuery: (query) => set({ searchQuery: query }),

      search: async (query: string) => {
        if (!query.trim()) return;
        set({ searching: true, searchError: null, searchQuery: query });

        const { semanticScholarApiKey } = get();
        let url = `/api/v1/citations/search?q=${encodeURIComponent(query)}`;
        if (semanticScholarApiKey) {
          url += `&s2_api_key=${encodeURIComponent(semanticScholarApiKey)}`;
        }

        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
          try {
            const resp = await api.get<{ data: SearchResult[]; error?: string }>(url);
            if (resp.error && isRateLimitError(resp.error) && attempt < MAX_RETRIES) {
              const delay = BASE_DELAY_MS * 2 ** attempt;
              set({ searchError: `Rate limited — retrying in ${Math.round(delay / 1000)}s...` });
              await sleep(delay);
              continue;
            }
            if (resp.error) {
              set({ searchResults: [], searchError: resp.error, searching: false });
            } else {
              set({ searchResults: resp.data || [], searchError: null, searching: false });
            }
            return;
          } catch (err) {
            if (attempt < MAX_RETRIES) {
              const delay = BASE_DELAY_MS * 2 ** attempt;
              set({ searchError: `Request failed — retrying in ${Math.round(delay / 1000)}s...` });
              await sleep(delay);
              continue;
            }
            const message = err instanceof Error ? err.message : "Search failed";
            set({ searchResults: [], searchError: message, searching: false });
            return;
          }
        }
      },

      addCitation: async (paper: SearchResult): Promise<string | null> => {
        set({ addError: null });
        try {
          const resp = await api.post<{ bibtex: string }>("/api/v1/citations/to-bibtex", {
            title: paper.title,
            authors: paper.authors,
            year: paper.year != null ? String(paper.year) : undefined,
            doi: paper.doi ?? undefined,
            journal: paper.journal ?? undefined,
          });
          const key = parseBibtexKey(resp.bibtex);
          if (key) {
            const { bibEntries } = get();
            if (!bibEntries.some((e) => e.key === key)) {
              set({
                bibEntries: [
                  ...bibEntries,
                  {
                    type: "article",
                    key,
                    title: paper.title,
                    authors: paper.authors,
                    year: paper.year != null ? String(paper.year) : null,
                    journal: paper.journal ?? null,
                    doi: paper.doi ?? null,
                    url: paper.url ?? null,
                    abstract: paper.abstract ?? null,
                  },
                ],
              });
            }
          }
          return resp.bibtex;
        } catch (err) {
          const message = err instanceof Error ? err.message : "Failed to generate BibTeX";
          set({ addError: message });
          return null;
        }
      },

      removeCitation: (key: string) => {
        set((s) => ({
          bibEntries: s.bibEntries.filter((e) => e.key !== key),
        }));
      },

      clearSearch: () => set({ searchResults: [], searchQuery: "", searchError: null }),

      clearAddError: () => set({ addError: null }),

      parseDocumentCitations: (content: string) => {
        set({ documentCiteKeys: extractCiteKeys(content) });
      },

      setCitationStyle: (style) => set({ citationStyle: style }),

      setSemanticScholarApiKey: (key) => set({ semanticScholarApiKey: key }),
    }),
    {
      name: "lever-citations",
      partialize: (state) => ({
        citationStyle: state.citationStyle,
        semanticScholarApiKey: state.semanticScholarApiKey,
        bibEntries: state.bibEntries,
      }),
    },
  ),
);
